import React from "react";
import {Chip, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import type {Editor} from "@tiptap/core";
import {useTranslation} from "react-i18next";
import {
    findPrintoutVariable,
    getPrintoutVariableDefaultFallback,
    getPrintoutVariableDisplayReference,
    normalizePrintoutDateFormat,
} from "../../../variables/printoutVariables";
import type {PrintoutDateFormat} from "../../../variables/printoutVariables";
import type {PrintoutVariableNodeAttrs} from "../../../variables/PrintoutVariableNode";
import {
    hasPrintoutVariableMark,
    PRINTOUT_VARIABLE_FORMAT_MARKS,
    togglePrintoutVariableMark,
    type PrintoutVariableFormatMark,
} from "../../toolbar/printoutToolbarState";
import {PanelField} from "../PrintoutPanelSection";
import type {SelectedPrintoutNode} from "../useSelectedPrintoutBlock";
import {updateSelectedPrintoutBlockAttributes} from "./printoutBlockAttributes";
import {DATE_FORMAT_OPTIONS} from "./printoutVariableFormatOptions";

const FORMAT_MARK_ICONS: Record<PrintoutVariableFormatMark, React.ReactNode> = {
    bold: <FormatBoldIcon fontSize={'small'}/>,
    italic: <FormatItalicIcon fontSize={'small'}/>,
    strike: <StrikethroughSIcon fontSize={'small'}/>,
    underline: <FormatUnderlinedIcon fontSize={'small'}/>,
};

const FORMAT_MARK_LABELS: Record<PrintoutVariableFormatMark, string> = {
    bold: 'Bold',
    italic: 'Italic',
    strike: 'Strike',
    underline: 'Underline',
};

interface Props {
    disabled?: boolean;
    editor: Editor;
    variable: SelectedPrintoutNode;
}

export function VariableSettings({disabled, editor, variable}: Props) {
    const {t} = useTranslation();

    // Catalogue strings may contain colons; never treat them as i18next namespaces.
    const catalogText = (text: string) => t(text, {nsSeparator: false});

    const attrs = variable.node.attrs as PrintoutVariableNodeAttrs;
    const reference = attrs.key
        ? (attrs.language ? `${attrs.key}:${attrs.language}` : attrs.key)
        : '';
    const displayReference = getPrintoutVariableDisplayReference(reference);
    const definition = attrs.key ? findPrintoutVariable(attrs.key) : undefined;
    const defaultFallback = getPrintoutVariableDefaultFallback(definition);
    const fallbackValue = typeof attrs.fallback === 'string' ? attrs.fallback : '';
    const sourceLabel = definition?.attr.path
        ? `${t('Source')}: ${definition.attr.path}`
        : definition?.attr.compute
            ? `${t('Computed')}: ${definition.attr.compute}`
            : '';

    const update = (next: Partial<PrintoutVariableNodeAttrs>) =>
        updateSelectedPrintoutBlockAttributes(editor, variable, next);

    const activeFormatMarks = PRINTOUT_VARIABLE_FORMAT_MARKS
        .filter(mark => hasPrintoutVariableMark(variable.node, mark));

    return (
        <>
            <Stack gap={0.75}>
                <Stack alignItems={'center'} direction={'row'} flexWrap={'wrap'} gap={1}>
                    <Typography variant={'body2'} fontWeight={600}>
                        {definition ? catalogText(definition.label) : displayReference}
                    </Typography>
                    {!!attrs.language && (
                        <Chip label={attrs.language.toUpperCase()} size={'small'} variant={'outlined'}/>
                    )}
                </Stack>
                <Typography color={'text.secondary'} variant={'body2'}>
                    {definition
                        ? catalogText(definition.description)
                        : t('This variable is not in the catalogue — it prints its fallback.')}
                </Typography>
            </Stack>

            <Stack gap={0.25}>
                {!!definition && (
                    <Typography color={'text.secondary'} variant={'caption'}>
                        {t('Scope')}: {catalogText(definition.scopeLabel)}
                    </Typography>
                )}
                <Typography color={'text.secondary'} variant={'caption'} sx={{wordBreak: 'break-all'}}>
                    {t('Reference')}: {displayReference}
                </Typography>
                {!!sourceLabel && (
                    <Typography color={'text.secondary'} variant={'caption'} sx={{wordBreak: 'break-all'}}>
                        {sourceLabel}
                    </Typography>
                )}
            </Stack>

            <PanelField label={t('Formatting')}>
                <ToggleButtonGroup
                    disabled={disabled}
                    // One changed mark per transaction keeps undo granular.
                    onChange={(_event, next: PrintoutVariableFormatMark[]) => {
                        const changed = PRINTOUT_VARIABLE_FORMAT_MARKS
                            .find(mark => next.includes(mark) !== activeFormatMarks.includes(mark));

                        if (changed) togglePrintoutVariableMark(editor, variable, changed);
                    }}
                    size={'small'}
                    value={activeFormatMarks}
                >
                    {PRINTOUT_VARIABLE_FORMAT_MARKS.map(mark => (
                        <ToggleButton aria-label={t(FORMAT_MARK_LABELS[mark])} key={mark} value={mark}>
                            {FORMAT_MARK_ICONS[mark]}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </PanelField>

            {definition?.attr.kind === 'date' && (
                <PanelField label={t('Date format')}>
                    <TextField
                        disabled={disabled}
                        fullWidth
                        helperText={t('Month and weekday names use the printout language.')}
                        onChange={(event) => update({dateFormat: event.target.value as PrintoutDateFormat})}
                        select
                        size={'small'}
                        value={normalizePrintoutDateFormat(attrs.dateFormat) ?? 'numeric'}
                    >
                        {DATE_FORMAT_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {t(option.label)}
                            </MenuItem>
                        ))}
                    </TextField>
                </PanelField>
            )}

            <PanelField label={t('Fallback when empty')}>
                <TextField
                    disabled={disabled}
                    fullWidth
                    helperText={t('Printed when this variable has no value. Leave it empty for the default.')}
                    onChange={(event) => {
                        const next = event.target.value;

                        update({fallback: next || null});
                    }}
                    placeholder={defaultFallback || t('No fallback')}
                    size={'small'}
                    value={fallbackValue}
                />
            </PanelField>
        </>
    );
}

export default VariableSettings;
